/**
 * Session Validation Utilities
 *
 * Helpers to validate and diagnose session generation issues.
 */

import { ModuleId } from '../types'
import { GroupId } from '../config/systemConfig'
import { getFamiliesForModule } from '../config/taxonomyRegistry'
import { getAllowedTiersForGroup, getTierRangeForGroup } from '../config/tiers'
import { QUESTIONS_BY_FAMILY } from '../data/questionLoader'
import { debug, warn, error as logError, group, groupEnd } from './logger'
export interface SessionValidationReport {
  moduleId: ModuleId
  group: GroupId
  isValid: boolean
  errors: string[]
  warnings: string[]
  stats: {
    totalFamilies: number
    eligibleFamilies: number
    familiesWithQuestions: number
    totalQuestionsAvailable: number
    tierRange: {
      min: number
      max: number
    }
  }
  familyDetails: {
    familyId: string
    tier: number
    isEligible: boolean
    questionCount: number
    questionsInTier: number
  }[]
}

/**
 * Validate if a module can generate a session for a given group.
 * Returns a detailed report for debugging.
 */
export function validateSessionAvailability(
  moduleId: ModuleId,
  group: GroupId,
): SessionValidationReport {
  const report: SessionValidationReport = {
    moduleId,
    group,
    isValid: false,
    errors: [],
    warnings: [],
    stats: {
      totalFamilies: 0,
      eligibleFamilies: 0,
      familiesWithQuestions: 0,
      totalQuestionsAvailable: 0,
      tierRange: {
        min: 0,
        max: 0,
      },
    },
    familyDetails: [],
  }

  // Get module families
  const allFamilies = getFamiliesForModule(moduleId)
  report.stats.totalFamilies = allFamilies.length
  if (allFamilies.length === 0) {
    report.errors.push(`No families found in taxonomy for module ${moduleId}`)
    return report
  }

  // Get allowed tiers for group
  const allowedTiers = getAllowedTiersForGroup(group)
  const tierRange = getTierRangeForGroup(group)
  report.stats.tierRange = tierRange

  // Filter eligible families
  const eligibleFamilies = allFamilies.filter((f) => allowedTiers.has(f.tier))
  report.stats.eligibleFamilies = eligibleFamilies.length
  if (eligibleFamilies.length === 0) {
    report.errors.push(
      `No families within allowed tiers [${tierRange.min}-${tierRange.max}] for group ${group}`,
    )
    report.warnings.push(
      `Module has ${allFamilies.length} families, but none match the group's tier range`,
    )
    return report
  }

  // Check question availability for each family
  allFamilies.forEach((family) => {
    const isEligible = allowedTiers.has(family.tier)
    const familyQuestions = QUESTIONS_BY_FAMILY.get(family.familyId) || []
    const questionsInTier = familyQuestions.filter((q) =>
      allowedTiers.has(q.tier),
    ).length
    report.familyDetails.push({
      familyId: family.familyId,
      tier: family.tier,
      isEligible,
      questionCount: familyQuestions.length,
      questionsInTier,
    })
    if (isEligible && questionsInTier > 0) {
      report.stats.familiesWithQuestions++
      report.stats.totalQuestionsAvailable += questionsInTier
    }
  })

  // Validation
  if (report.stats.familiesWithQuestions === 0) {
    report.errors.push(
      `No eligible families have questions within allowed tiers [${tierRange.min}-${tierRange.max}]`,
    )
    report.warnings.push(
      `This indicates missing question content for this module/group combination`,
    )
  } else if (report.stats.totalQuestionsAvailable < 10) {
    report.warnings.push(
      `Only ${report.stats.totalQuestionsAvailable} questions available (recommended: 10+)`,
    )
  }
  report.isValid =
    report.errors.length === 0 && report.stats.familiesWithQuestions > 0
  return report
}

/**
 * Log a validation report to console (for debugging).
 */
export function logValidationReport(report: SessionValidationReport): void {
  group(`📊 Session Validation: ${report.moduleId} (Group ${report.group})`)
  debug(`Status: ${report.isValid ? '✅ Valid' : '❌ Invalid'}`)
  if (report.errors.length > 0) {
    group('❌ Errors:')
    report.errors.forEach((e) => logError(`  ${e}`))
    groupEnd()
  }
  if (report.warnings.length > 0) {
    group('⚠️ Warnings:')
    report.warnings.forEach((w) => warn(`  ${w}`))
    groupEnd()
  }
  group('📈 Statistics:')
  debug(`  Total families: ${report.stats.totalFamilies}`)
  debug(`  Eligible families: ${report.stats.eligibleFamilies}`)
  debug(`  Families with questions: ${report.stats.familiesWithQuestions}`)
  debug(`  Total questions available: ${report.stats.totalQuestionsAvailable}`)
  debug(
    `  Tier range: [${report.stats.tierRange.min}-${report.stats.tierRange.max}]`,
  )
  groupEnd()
  if (report.familyDetails.length > 0 && !report.isValid) {
    group('🔍 Family Details:')
    report.familyDetails.forEach((fd) => {
      const status = fd.isEligible
        ? fd.questionsInTier > 0
          ? '✅'
          : '⚠️ No questions'
        : '❌ Out of tier range'
      debug(
        `  ${status} ${fd.familyId} (tier ${fd.tier}): ${fd.questionsInTier}/${fd.questionCount} questions available`,
      )
    })
    groupEnd()
  }
  groupEnd()
}

/**
 * Quick check if a module has content for a group (for UI).
 */
export function hasContentForGroup(
  moduleId: ModuleId,
  group: GroupId,
): boolean {
  const report = validateSessionAvailability(moduleId, group)
  return report.isValid
}
